/**
 * 侧栏菜单图标解析：将路由配置里的 `icon` 字符串（Ant 图标 key 或 `icon-` 前缀的 iconfont）渲染为 React 节点。
 * 登记表见 {@link ANT_ICON_BY_ROUTE_KEY}；与 `menuItemRenderDeepIcon`、本地 `assets/iconfont` 配合。
 */
import { createFromIconfontCN } from '@ant-design/icons';
import {
  BulbOutlined,
  EditOutlined,
  FileSearchOutlined,
  FireOutlined,
  HighlightOutlined,
  HomeOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  ScissorOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import defaultSettings from '../../../config/defaultSettings';
import React from 'react';

/** 远程 Symbol 脚本与 defaultSettings.iconfontUrl 一致；未配置时 `icon-xxx` 走本地 `iconfont.css` 的 class */
const IconFont = createFromIconfontCN({
  scriptUrl: defaultSettings.iconfontUrl || '',
});

const ANT_ICON_BY_ROUTE_KEY: Record<string, React.ReactNode> = {
  home: <HomeOutlined />,
  fileSearch: <FileSearchOutlined />,
  fire: <FireOutlined />,
  highlight: <HighlightOutlined />,
  picture: <PictureOutlined />,
  /** 与路由里常用别名 `image` 对应（如三级菜单） */
  image: <PictureOutlined />,
  videoCamera: <VideoCameraOutlined />,
  bulb: <BulbOutlined />,
  edit: <EditOutlined />,
  playCircle: <PlayCircleOutlined />,
  scissor: <ScissorOutlined />,
};

export type MenuRouteIconProps = {
  icon: string;
  className?: string;
};

/**
 * Pro 路由 `icon` 字符串解析：
 * - Ant：小驼峰 key → Outlined（见 `ANT_ICON_BY_ROUTE_KEY`）
 * - Iconfont：`icon-xxx` — 已配 `iconfontUrl` 时用 Symbol；否则用全局引入的 `src/assets/iconfont/iconfont.css`（`iconfont` + 项目类名）
 */
export const MenuRouteIcon: React.FC<MenuRouteIconProps> = ({
  icon,
  className,
}) => {
  if (!icon) return null;
  if (icon.startsWith('icon-')) {
    if (defaultSettings.iconfontUrl) {
      return <IconFont type={icon} className={className} />;
    }
    return (
      <span
        className={['iconfont', icon, className].filter(Boolean).join(' ')}
        aria-hidden
      />
    );
  }
  return (ANT_ICON_BY_ROUTE_KEY[icon] ?? null) as React.ReactElement | null;
};

